import Cookies from 'js-cookie';

const PERMISSION_KEY_TO_MODULE_ACTION = {
  dashboard: [['ui', 'dashboard']],
  report_view: [['reports', 'view']],
  report_admin: [['reports', 'admin'], ['reports', 'edit'], ['reports', 'create']],
  sales_view: [['sales', 'view']],
  event_view: [['events', 'view']],
  institution_view: [['facilities', 'view']],
  shift_view: [['shifts', 'view']],
  shift_admin: [['shifts', 'admin'], ['shifts', 'edit'], ['shifts', 'create']],
  analytics_view: [['analytics', 'view']],
  analytics_admin: [['analytics', 'admin']],
  invoice_view: [['invoices', 'view']],
  invoice_admin: [['invoices', 'admin'], ['invoices', 'edit'], ['invoices', 'create']],
  notices_view: [['notifications', 'view']],
  notices_admin: [['notifications', 'admin'], ['notifications', 'edit'], ['notifications', 'create'], ['notifications', 'delete']],
  tests_view: [['tests', 'view']],
  tests_admin: [['tests', 'admin']],
  learning_view: [['materials', 'view'], ['learning', 'view']],
  learning_admin: [['materials', 'admin'], ['materials', 'edit'], ['materials', 'create']],
  attendance_admin: [['attendance', 'admin']],
  users_admin: [['users', 'admin'], ['users', 'manage_permissions'], ['users', 'manage_roles']]
};

const DEFAULT_VIEW_PERMISSION_KEYS = [
  'dashboard',
  'report_view',
  'sales_view',
  'event_view',
  'institution_view',
  'shift_view',
  'analytics_view',
  'invoice_view',
  'notices_view',
  'tests_view',
  'learning_view'
];

const ROLE_FALLBACK_PERMISSION_KEYS = {
  view_admin: [...DEFAULT_VIEW_PERMISSION_KEYS, 'analytics_admin', 'tests_admin', 'attendance_admin'],
  contractor: DEFAULT_VIEW_PERMISSION_KEYS,
  user: DEFAULT_VIEW_PERMISSION_KEYS
};

const normalizePermissionKeys = (rawPermissions) => {
  if (!Array.isArray(rawPermissions)) {
    return [];
  }

  const normalizedKeys = new Set(rawPermissions.filter((key) => PERMISSION_KEY_TO_MODULE_ACTION[key]));
  const moduleActionSet = new Set(rawPermissions.filter((key) => typeof key === 'string' && key.includes('/')));

  if (moduleActionSet.size > 0) {
    Object.entries(PERMISSION_KEY_TO_MODULE_ACTION).forEach(([permissionKey, pairs]) => {
      const hasAllPairs = pairs.every(([module, action]) => moduleActionSet.has(`${module}/${action}`));
      if (hasAllPairs) {
        normalizedKeys.add(permissionKey);
      }
    });
  }

  return Array.from(normalizedKeys);
};

/**
 * ユーザー情報を取得
 * @returns {Object|null} ユーザー情報
 */
export const getUserInfo = () => {
  try {
    const userInfoCookie = Cookies.get('user_info');
    if (userInfoCookie) {
      return JSON.parse(userInfoCookie);
    }
  } catch (error) {
    console.error('Failed to parse user info:', error);
  }
  return null;
};

/**
 * ユーザーのロールを取得
 * @returns {string|null} ロール
 */
export const getUserRole = () => {
  const userInfo = getUserInfo();
  return userInfo?.role || null;
};

/**
 * 管理者権限チェック
 * @returns {boolean} 管理者かどうか
 */
export const isAdmin = () => {
  const role = getUserRole();
  return role === 'admin' || role === 'view_admin';
};

/**
 * 特定のロールを持っているかチェック
 * @param {string|string[]} allowedRoles - 許可されたロール（配列または単一の文字列）
 * @returns {boolean} 許可されたロールを持っているか
 */
export const hasRole = (allowedRoles) => {
  const role = getUserRole();
  if (!role) return false;
  
  if (Array.isArray(allowedRoles)) {
    return allowedRoles.includes(role);
  }
  return role === allowedRoles;
};

/**
 * 認証トークンを取得
 * @returns {string|null} トークン
 */
export const getAuthToken = () => {
  return Cookies.get('auth_token') || null;
};

/**
 * ログアウト処理
 */
export const logout = () => {
  Cookies.remove('auth_token');
  Cookies.remove('user_info');
  Cookies.remove('token_info');
  window.location.href = '/login';
};

/**
 * バックエンドAPIリクエスト用のヘッダーを取得
 * @returns {Object} ヘッダー
 */
export const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

/**
 * 権限付きAPIリクエストのラッパー
 * バックエンドで権限検証が行われることを前提とする
 * @param {string} url - APIエンドポイント
 * @param {Object} options - fetchオプション
 * @returns {Promise<Response>} レスポンス
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('認証トークンがありません');
  }
  
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  // 認証エラーの場合はログアウト
  if (response.status === 401 || response.status === 403) {
    logout();
    throw new Error('認証エラー: ログインし直してください');
  }
  
  return response;
};

/**
 * ユーザーが特定の権限を持つかチェック
 * @param {string} module - モジュール名（例: 'materials'）
 * @param {string} action - アクション名（例: 'edit'）
 * @returns {Promise<boolean>} 権限を持つかどうか
 */
export const checkPermission = async (module, action) => {
  try {
    const token = getAuthToken();
    const userInfo = getUserInfo();
    
    if (!token || !userInfo) {
      return false;
    }
    
    // superadmin は無条件に全権限
    if (userInfo.role === 'superadmin') {
      return true;
    }
    
    const response = await fetch(
      `https://system.sales-management.cloud/api/permissions.php?action=check_permission&user_id=${userInfo.id}&module=${module}&action=${action}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );
    
    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    return data.status === 'success' && data.data.has_permission;
  } catch (error) {
    console.error('Permission check error:', error);
    return false;
  }
};

/**
 * ユーザーが特定のサイドバー権限を持つかチェック（permissionKey ベース）
 * @param {string} permissionKey - 権限キー（例: 'dashboard', 'report_view', 'sales_admin'）
 * @returns {boolean} 権限を持つかどうか
 */
export const checkPermissionKey = (permissionKey) => {
  try {
    const userInfo = getUserInfo();
    
    if (!userInfo) {
      return false;
    }
    
    // superadmin は無条件に全権限
    if (userInfo.role === 'superadmin') {
      return true;
    }

    // admin は全メニューを許可（UI側）
    if (userInfo.role === 'admin') {
      return true;
    }
    
    // localStorage から権限キャッシュを取得
    const permissionsStr = localStorage.getItem(`user_permissions_${userInfo.id}`);
    if (permissionsStr) {
      const permissions = normalizePermissionKeys(JSON.parse(permissionsStr));
      if (permissions.length > 0) {
        return permissions.includes(permissionKey);
      }
    }
    
    // キャッシュがない場合はロールの既定表示にフォールバック
    const fallbackKeys = ROLE_FALLBACK_PERMISSION_KEYS[userInfo.role] || [];
    return fallbackKeys.includes(permissionKey);
  } catch (error) {
    console.error('Permission key check error:', error);
    return false;
  }
};

/**
 * ユーザーの権限キャッシュを更新
 * @param {Array} permissionKeys - 権限キーの配列
 */
export const updatePermissionCache = (permissionKeys) => {
  try {
    const userInfo = getUserInfo();
    if (userInfo) {
      const normalized = normalizePermissionKeys(permissionKeys);
      localStorage.setItem(`user_permissions_${userInfo.id}`, JSON.stringify(normalized));
    }
  } catch (error) {
    console.error('Error caching permissions:', error);
  }
};

/**
 * ユーザーの権限一覧を取得
 * @returns {Promise<Array>} 権限の配列
 */
export const getUserPermissions = async () => {
  try {
    const token = getAuthToken();
    const userInfo = getUserInfo();
    
    if (!token || !userInfo) {
      return [];
    }
    
    const response = await fetch(
      `https://system.sales-management.cloud/api/permissions.php?action=get_user_permissions&user_id=${userInfo.id}&include_role_permissions=1`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    return data.status === 'success' ? data.data.permissions || [] : [];
  } catch (error) {
    console.error('Get user permissions error:', error);
    return [];
  }
};
